import React, { useEffect, useState } from 'react'
import { formateDateWithTime } from '../../../helpers/commonFunctions'
import { GetCompanyList } from '../../../services/companyService'

// Presentational bits shared by every queue's job view. Nothing here talks to a
// queue endpoint — see useQueue.js for that.

export const formatLabel = (key) => {
    return String(key).replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
}

export const normalizeKey = (k) => String(k).toLowerCase().replace(/[_\s-]/g, '')

export const isEmpty = (v) => v == null || v === '' || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) || (Array.isArray(v) && v.length === 0)

// Payload timestamps arrive as ISO strings in UTC; the panel shows dates in the
// viewer's local time everywhere else, so these are formatted the same way.
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/

export const renderScalar = (val) => {
    if (val == null || val === '') return <span className="text-muted">—</span>
    if (typeof val === 'boolean') return <span className={`badge ${val ? 'bg-success' : 'bg-secondary'}`}>{String(val)}</span>
    if (typeof val === 'string' && ISO_TIMESTAMP.test(val)) return <span>{formateDateWithTime(val)}</span>
    return <span style={{ wordBreak: 'break-word' }}>{String(val)}</span>
}

export const Section = ({ title, children, action }) => (
    <div className="card border mb-3">
        <div className="card-header d-flex justify-content-between align-items-center py-2 px-3" style={{ background: '#f8f9fc' }}>
            <h6 className="mb-0" style={{ color: '#403fad', fontWeight: 600 }}>{title}</h6>
            {action}
        </div>
        <div className="card-body p-3">{children}</div>
    </div>
)

export const KvRow = ({ label, value, full }) => (
    <div className={`mb-2 ${full ? 'col-12' : 'col-md-6'}`}>
        <div className="text-muted small">{label}</div>
        <div style={{ wordBreak: 'break-word' }}>{value}</div>
    </div>
)

// The queue stores JSON inside JSON: fields like menu_items_id and service_type
// arrive already encoded, so a plain stringify renders them as one escaped line.
// Parsing them back (at any depth) turns the dump into a readable tree.
export const expandJsonStrings = (value) => {
    if (Array.isArray(value)) return value.map(expandJsonStrings)
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, expandJsonStrings(v)]))
    }
    if (typeof value === 'string') {
        const trimmed = value.trim()
        const looksLikeJson = (trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'))
        if (looksLikeJson) {
            try {
                return expandJsonStrings(JSON.parse(trimmed))
            } catch (error) {
                return value
            }
        }
    }
    return value
}

// Indented JSON for a value that is itself an encoded object/array, else null.
export const prettyJson = (str) => {
    const trimmed = String(str).trim()
    const looksLikeJson = (trimmed.startsWith('{') && trimmed.endsWith('}'))
        || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    if (!looksLikeJson) return null
    try {
        return JSON.stringify(expandJsonStrings(JSON.parse(trimmed)), null, 2)
    } catch (error) {
        return null
    }
}

export const JsonBlock = ({ text, maxHeight = 240 }) => (
    <pre className="p-2 mb-0 border rounded bg-light" style={{
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight,
        overflow: 'auto',
    }}>
        {text}
    </pre>
)

export const renderKvGrid = (entries, fullKeys = [], labelMap = {}) => {
    const fullSet = new Set(fullKeys.map(normalizeKey))
    return (
        <div className="row">
            {entries.map(([k, v]) => {
                const str = v == null ? '' : String(v)
                const pretty = prettyJson(str)
                const isLong = pretty != null || str.length > 60
                return (
                    <KvRow
                        key={k}
                        label={labelMap[normalizeKey(k)] || formatLabel(String(k))}
                        value={
                            pretty != null ? <JsonBlock text={pretty} />
                                : isLong ? (
                                    <div className="p-2 border rounded bg-light" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', maxHeight: 160, overflow: 'auto' }}>
                                        {str}
                                    </div>
                                ) : renderScalar(v)
                        }
                        full={fullSet.has(normalizeKey(k)) || isLong}
                    />
                )
            })}
        </div>
    )
}

// Job names are identifiers, not prose — the same chip on every queue so a
// name reads the same whether it says "insert" or "syncErpReports".
export const JobNameBadge = ({ name, title }) => (
    <span className="queue-job-name" title={title || ''}>{name || 'N/A'}</span>
)

export const decodeJwtPayload = (token) => {
    try {
        const raw = String(token || '').replace(/^Bearer\s+/i, '')
        const part = raw.split('.')[1]
        if (!part) return null
        return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
    } catch (error) {
        return null
    }
}

// A job names its tenant either through the caller's token or through the tenant
// database it was queued against; both resolve against the admin company list.
export const useCompanies = () => {
    const [companies, setCompanies] = useState([])

    useEffect(() => {
        const formData = new FormData()
        formData.append("page", 1)
        formData.append("per_page", 1000)
        formData.append("sort", JSON.stringify({}))
        formData.append("search", JSON.stringify({}))
        formData.append("global_search", "")
        GetCompanyList(formData).then(data => {
            setCompanies(data?.data?.response?.rows || [])
        }).catch(error => console.log("company list error ====> ", error))
    }, [])

    return companies
}
